import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { Guard } from '../lib/delete-guards';
import { AlertTriangle, ShieldAlert, Trash2, X, Loader2 } from 'lucide-react';

/**
 * Confirmation modal before something irreversible.
 *
 * When a `check` is supplied, the dialog asks whether the record can actually
 * be removed BEFORE offering a Delete button. If something is in the way it
 * says exactly what — "This project has 1 payment recorded against it" — and
 * offers only Close.
 */
export default function Confirm({
  title,
  message,
  confirmLabel = 'Delete',
  danger = true,
  busy,
  check,
  onConfirm,
  onCancel,
  children,
}: {
  title: string;
  message: string;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  check?: Guard;
  onConfirm: () => void;
  onCancel: () => void;
  children?: ReactNode;
}) {
  const [checking, setChecking] = useState(!!check);
  const [blocked, setBlocked] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  useEffect(() => {
    if (!check) return;
    let alive = true;
    setChecking(true);
    check()
      .then((reason) => {
        if (alive) {
          setBlocked(reason);
          setChecking(false);
        }
      })
      .catch(() => {
        if (alive) {
          setBlocked(null);
          setChecking(false);
        }
      });
    return () => {
      alive = false;
    };
  }, [check]);

  return createPortal(
    <>
      <div className="scrim" onClick={onCancel} />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        className="confirm-dialog"
      >
        <div className="confirm-dialog-body">
          <div
            className={`confirm-dialog-icon ${
              blocked ? 'warning' : danger ? 'danger' : 'info'
            }`}
          >
            {checking ? (
              <Loader2 className="animate-spin" size={20} />
            ) : blocked ? (
              <ShieldAlert size={22} />
            ) : danger ? (
              <Trash2 size={22} />
            ) : (
              <AlertTriangle size={22} />
            )}
          </div>

          <div className="confirm-dialog-content">
            <h3 className="confirm-dialog-title">
              {blocked ? 'Action Blocked' : title}
            </h3>

            {checking ? (
              <p className="confirm-dialog-text" style={{ color: 'var(--text-faint)' }}>
                Checking database references & dependencies…
              </p>
            ) : blocked ? (
              <div className="confirm-dialog-blocked">
                {blocked}
              </div>
            ) : (
              <p className="confirm-dialog-text">{message}</p>
            )}

            {!blocked && !checking && children && (
              <div style={{ marginTop: 12 }}>{children}</div>
            )}
          </div>

          <button
            type="button"
            onClick={onCancel}
            className="confirm-dialog-close"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="confirm-dialog-foot">
          <button
            type="button"
            className="btn btn-auto btn-sm btn-ghost"
            onClick={onCancel}
            disabled={busy}
            autoFocus={!!blocked}
          >
            {blocked ? 'Close' : 'Cancel'}
          </button>

          {!blocked && (
            <button
              type="button"
              className={`btn btn-auto btn-sm ${danger ? 'btn-danger' : 'btn-primary'}`}
              onClick={onConfirm}
              disabled={busy || checking}
              autoFocus
            >
              {busy ? (
                <>
                  <Loader2 className="animate-spin" size={14} />
                  <span>Processing…</span>
                </>
              ) : checking ? (
                <span>Checking…</span>
              ) : (
                <span>{confirmLabel}</span>
              )}
            </button>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}

/** Small hook so a list can drive one dialog for whichever row was clicked. */
export function useConfirm<T>() {
  const [target, setTarget] = useState<T | null>(null);
  return {
    target,
    ask: (t: T) => setTarget(t),
    close: () => setTarget(null),
  };
}
